import { Fragment, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import { date, dateTime, percent } from '@/lib/format'
import { contractYears, productsOf, rateAt } from '@/lib/commission'
import { combine, useAsync } from '@/lib/useAsync'
import { Dialog } from '@/components/ui/dialog'
import { Field, Input } from '@/components/ui/form'
import { PageHeader } from '@/components/ui/page'
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Loading,
} from '@/components/ui/primitives'
import { Table, Td, Th, Tr } from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import type {
  BankAccountProposal,
  PortalBankAccount,
  PortalChangeRequest,
  PortalCommissionPlan,
  PortalProfile,
  ProfileProposal,
  ResellerChangeKind,
} from '@/types/api'

/**
 * The partner's own record, and the one screen with a button that writes.
 *
 * It writes a request, and a request changes nothing. The threat this shape exists for is
 * not a stolen database - Azure SQL encrypts at rest - it is a changed number: whoever
 * compromises a portal account wants to write a new bank account, and the next payment
 * goes to them. So the account in force stays in force until somebody at Codemax approves
 * the change, and that approver confirms it by telephoning the number already on file
 * rather than the one in the request.
 */
export function Company() {
  const profile = useAsync<PortalProfile>(
    (signal) => api.get<PortalProfile>('/portal/profile', undefined, signal),
    [],
  )
  const accounts = useAsync<PortalBankAccount[]>(
    (signal) => api.get<PortalBankAccount[]>('/portal/bank-accounts', undefined, signal),
    [],
  )
  const requests = useAsync<PortalChangeRequest[]>(
    (signal) => api.get<PortalChangeRequest[]>('/portal/change-requests', undefined, signal),
    [],
  )
  const plans = useAsync<PortalCommissionPlan[]>(
    (signal) => api.get<PortalCommissionPlan[]>('/portal/commission-plans', undefined, signal),
    [],
  )

  const [editing, setEditing] = useState<ResellerChangeKind | undefined>(undefined)

  const state = combine(profile, accounts, requests)

  // The three the screen cannot draw without, not the plans - those load beside it and
  // report their own failure, because a commission grid that could not be fetched is no
  // reason to take the bank account off the screen. The dialogs below read their opening
  // values once, when they mount, so drawing this screen while the bank accounts were still
  // in flight left the change-of-account form with an empty currency for the rest of the
  // session. Once loaded the data survives a reload - `useAsync` keeps the last good value -
  // so this spinner appears exactly once and never flashes between requests.
  const loaded = profile.data !== undefined && accounts.data !== undefined && requests.data !== undefined

  if (state.loading && !loaded) return <Loading />
  if (state.error) {
    return <ErrorState title={state.error.title} detail={state.error.detail} onRetry={state.reload} />
  }
  if (!profile.data || !accounts.data || !requests.data) return null

  const company = profile.data
  const bank = accounts.data
  const history = requests.data

  const active = bank.find((account) => account.status === 'Active')
  const proposed = bank.find((account) => account.status === 'Pending')

  // One pending request of each kind. Two competing proposals for the same bank account
  // is the shape a race is won in, and the API refuses the second with a 409 - so the
  // button that would earn that 409 is disabled rather than left to earn it.
  const pending = new Set(
    history.filter((r) => r.status === 'Pending').map((r) => r.kind),
  )

  function done() {
    setEditing(undefined)
    accounts.reload()
    requests.reload()
  }

  return (
    <>
      <PageHeader title="Company" hint={company.name} />

      {proposed && (
        <div
          role="status"
          className="mb-5 rounded-md border border-warn/30 bg-warn-wash px-4 py-3 text-[13px] text-ink-2"
        >
          <p className="font-medium text-warn">A bank change is waiting for approval.</p>
          <p className="mt-1">
            You asked us to pay into an account ending {proposed.lastFour}. Until a Codemax
            approver confirms it &mdash; by telephoning the number already on your file
            &mdash; payouts continue to {active ? `the account ending ${active.lastFour}` : 'your account in force'}.
          </p>
        </div>
      )}

      <Card className="mb-5">
        <CardHeader
          title="Company details"
          action={
            <Button
              size="sm"
              disabled={pending.has('Profile')}
              onClick={() => setEditing('Profile')}
            >
              {pending.has('Profile') ? 'Correction requested' : 'Request a correction'}
            </Button>
          }
        />

        <dl className="grid gap-x-8 gap-y-3 px-5 py-4 sm:grid-cols-2">
          {/* The name and the registration number are read-only here and there is no
              button that changes them. They identify the legal entity Codemax contracted
              with, so changing one is a new commercial relationship rather than a
              correction, and it goes through your Codemax contact with the paperwork. */}
          <Detail label="Registered name" value={company.name} />
          <Detail label="Registration no." value={company.registrationNo} />
          <Detail label="Tax ID" value={company.taxIdentificationNo} />
          <Detail label="Contact" value={company.contactName} />
          <Detail label="Email" value={company.email} />
          <Detail label="Phone" value={company.phone} />
          <Detail
            label="Address"
            value={[
              company.addressLine1,
              company.addressLine2,
              company.addressLine3,
              [company.postcode, company.addressCountry].filter(Boolean).join(' '),
            ]
              .filter((line) => line && line.trim() !== '')
              .join('\n')}
          />
          <Detail label="Country" value={company.countryIsoCode} />
        </dl>
      </Card>

      <Card className="mb-5">
        <CardHeader
          title="What you earn"
          hint="Commission is earned when the customer pays, at the rate frozen onto the quotation when it was raised."
        />

        {/* Three states, not two. "No commission plan yet" is a fact about the partner's
            agreement, and showing it for the second a request is in flight tells somebody
            with a plan that they have none. */}
        {plans.error ? (
          <div className="px-5 py-4">
            <ErrorState
              title={plans.error.title}
              detail={plans.error.detail}
              onRetry={plans.reload}
            />
          </div>
        ) : plans.data === undefined ? (
          <Loading />
        ) : plans.data.length === 0 ? (
          <EmptyState
            title="No commission plan yet"
            hint="Your rates are agreed with your Codemax contact. Once a plan is in force it appears here, and every statement you receive is worked out from it."
          />
        ) : (
          <div className="divide-y divide-line-soft">
            {plans.data.map((plan) => (
              <CommissionPlan key={plan.countryIsoCode} plan={plan} />
            ))}
          </div>
        )}
      </Card>

      <Card className="mb-5">
        <CardHeader
          title="Where we pay you"
          hint="One request at a time. Codemax will never ask for your account details by email."
          action={
            <Button
              size="sm"
              disabled={pending.has('BankAccount')}
              onClick={() => setEditing('BankAccount')}
            >
              {pending.has('BankAccount') ? 'Change requested' : 'Request a change'}
            </Button>
          }
        />

        {bank.length === 0 ? (
          <EmptyState
            title="No bank account on file"
            hint="Ask us to add one and a Codemax approver will confirm it with you by telephone before anything is paid to it."
          />
        ) : (
          <ul className="divide-y divide-line-soft">
            {bank.map((account) => (
              <li key={account.id} className="flex flex-wrap items-center gap-3 px-5 py-4">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">
                    {account.bankName}{' '}
                    <span className="font-normal text-muted">{account.currencyCode}</span>
                  </p>
                  <p className="text-[12px] text-muted">
                    {account.accountHolderName}
                    {account.branchName ? ` · ${account.branchName}` : ''}
                  </p>
                </div>
                <p className="tabular text-sm">
                  &bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull; {account.lastFour}
                </p>
                <AccountBadge status={account.status} />
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <CardHeader title="Requests you have made" />

        {history.length === 0 ? (
          <EmptyState title="Nothing requested yet" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Asked</Th>
                <Th>What</Th>
                <Th>Status</Th>
                <Th>Reviewed</Th>
                <Th>Note from Codemax</Th>
              </tr>
            </thead>
            <tbody>
              {history.map((request) => (
                <Tr key={request.id}>
                  <Td className="whitespace-nowrap">{date(request.requestedAt)}</Td>
                  <Td>{request.kind === 'BankAccount' ? 'Bank account' : 'Company details'}</Td>
                  <Td>
                    <RequestBadge status={request.status} />
                  </Td>
                  <Td className="whitespace-nowrap">
                    {request.reviewedAt ? (
                      dateTime(request.reviewedAt)
                    ) : (
                      <span className="text-faint">&mdash;</span>
                    )}
                  </Td>
                  <Td>{request.reviewNote ?? <span className="text-faint">&mdash;</span>}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <ProfileDialog
        open={editing === 'Profile'}
        profile={company}
        onClose={() => setEditing(undefined)}
        onDone={done}
      />

      <BankDialog
        open={editing === 'BankAccount'}
        currencyCode={active?.currencyCode ?? ''}
        onClose={() => setEditing(undefined)}
        onDone={done}
      />
    </>
  )
}

/**
 * One country's grid: five contract years by two kinds of money, per product.
 *
 * <p>
 * Here rather than on the Rate Card, because these are the partner's own terms rather than
 * Codemax's prices, and this is the screen that holds everything else about their company.
 * </p>
 * <p>
 * <b>Resolved, not as stored.</b> What a product pays is set on the product; a partner's own
 * plan carries only where they are entitled to something different. Showing the plan would show
 * a partner one product where they earn on the whole catalogue, so the API runs the same ladder
 * a submit does and this renders the answer.
 * </p>
 * <p>
 * An override grid is the same shape and says what this partner earns when a partner selling
 * under them wins a deal. It appears only for a partner who has one, so most read this screen
 * exactly as they did before.
 * </p>
 *
 * Nothing on this screen says what a given deal is worth. The grid is copied onto a
 * quotation at submit and the accrual reads only that copy, so a deal issued in 2026 keeps
 * 2026's rates however often this changes afterwards.
 */
function CommissionPlan({ plan }: { plan: PortalCommissionPlan }) {
  const years = contractYears(plan.rates)
  const products = productsOf(plan.rates)
  const isOverride = plan.tier === 'Override'

  // A resolved grid carries a catch-all row only where the partner holds one. Rendering an empty
  // "all products" band above the per-product rows would state a rate that does not exist.
  const hasBlanket = plan.rates.some((rate) => rate.productId === null)

  return (
    <section className="px-5 py-4">
      <div className="mb-3 flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
        <h3 className="text-sm font-medium">
          {isOverride ? `Deals won by ${plan.downlineResellerName}` : plan.countryName}
        </h3>
        <Badge tone={isOverride ? 'spark' : 'accent'}>
          {isOverride ? 'Override' : plan.countryIsoCode}
        </Badge>
        <span className="text-[12px] text-muted">In force since {date(plan.effectiveFrom)}</span>
      </div>

      {isOverride && (
        <p className="mb-3 text-[13px] text-ink-2">
          What you earn when {plan.downlineResellerName} wins a deal in {plan.countryName}, on
          top of what they earn themselves. You keep your own rates on your own deals.
        </p>
      )}

      <Table>
        <thead>
          <tr>
            <Th>Contract year</Th>
            {years.map((year) => (
              <Th key={year} numeric>
                {year}
              </Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {hasBlanket && (
            <>
              <RateRow
                label="One-time charges"
                years={years}
                rateOf={(year) => rateAt(plan.rates, year, 'OneTime')}
              />
              <RateRow
                label="Recurring charges"
                years={years}
                rateOf={(year) => rateAt(plan.rates, year, 'Recurring')}
              />
            </>
          )}

          {products.map((product) => (
            <Fragment key={product.id}>
              <tr>
                <td
                  colSpan={years.length + 1}
                  className="border-t border-line bg-surface-2 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted"
                >
                  {product.name}
                </td>
              </tr>
              <RateRow
                label="One-time charges"
                years={years}
                rateOf={(year) => rateAt(plan.rates, year, 'OneTime', product.id)}
              />
              <RateRow
                label="Recurring charges"
                years={years}
                rateOf={(year) => rateAt(plan.rates, year, 'Recurring', product.id)}
              />
            </Fragment>
          ))}
        </tbody>
      </Table>

      <p className="mt-3 text-[12px] text-muted">
        {products.length > 0 && !isOverride
          ? 'These are the rates you earn today, per product. '
          : ''}
        {products.length > 0 && isOverride
          ? 'The rows at the top apply to any product not listed below them. '
          : ''}
        Five years is a cap. A payment received after the fifth contract year earns nothing,
        and the statement line says so rather than leaving the figure out.
      </p>
    </section>
  )
}

/**
 * A zero is shown as 0.00%, never as a blank.
 *
 * A plan cannot be activated with a cell unfilled, so a zero here means "not entitled"
 * rather than "nobody typed it" - and a blank would say the second thing.
 */
function RateRow({
  label,
  years,
  rateOf,
}: {
  label: string
  years: number[]
  rateOf: (year: number) => number | undefined
}) {
  return (
    <Tr>
      <Td className="font-medium">{label}</Td>
      {years.map((year) => {
        const value = rateOf(year)

        return (
          <Td key={year} numeric className={value === 0 ? 'text-faint' : undefined}>
            {value === undefined ? <span className="text-faint">&mdash;</span> : percent(value)}
          </Td>
        )
      })}
    </Tr>
  )
}

function Detail({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <dt className="text-[12px] text-muted">{label}</dt>
      <dd className="whitespace-pre-line text-sm">
        {value && value.trim() !== '' ? value : <span className="text-faint">&mdash;</span>}
      </dd>
    </div>
  )
}

function AccountBadge({ status }: { status: PortalBankAccount['status'] }) {
  if (status === 'Active') return <Badge tone="positive">In force</Badge>
  if (status === 'Pending') return <Badge tone="warning">Awaiting approval</Badge>

  return <Badge>Replaced</Badge>
}

function RequestBadge({ status }: { status: PortalChangeRequest['status'] }) {
  if (status === 'Approved') return <Badge tone="positive">Approved</Badge>
  if (status === 'Rejected') return <Badge tone="critical">Rejected</Badge>

  return <Badge tone="warning">Pending</Badge>
}

function ProfileDialog({
  open,
  profile,
  onClose,
  onDone,
}: {
  open: boolean
  profile: PortalProfile
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<ProfileProposal>(() => ({
    contactName: profile.contactName,
    email: profile.email,
    phone: profile.phone,
    addressLine1: profile.addressLine1,
    addressLine2: profile.addressLine2,
    addressLine3: profile.addressLine3,
    postcode: profile.postcode,
    addressCountry: profile.addressCountry,
    taxIdentificationNo: profile.taxIdentificationNo,
  }))

  function set(key: keyof ProfileProposal, value: string) {
    setForm((current) => ({ ...current, [key]: value === '' ? null : value }))
  }

  async function send() {
    setSaving(true)
    try {
      await api.post('/portal/change-requests', { kind: 'Profile', profile: form })
      toast.show('Sent to Codemax', 'success')
      onDone()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not send that request', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      title="Request a correction"
      description="Nothing changes until somebody at Codemax approves it."
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={saving} onClick={() => void send()}>
            Send request
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Contact">
          {(id) => (
            <Input id={id} value={form.contactName ?? ''} onChange={(e) => set('contactName', e.target.value)} />
          )}
        </Field>
        <Field label="Email">
          {(id) => (
            <Input id={id} type="email" value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} />
          )}
        </Field>
        <Field label="Phone">
          {(id) => <Input id={id} value={form.phone ?? ''} onChange={(e) => set('phone', e.target.value)} />}
        </Field>
        <Field label="Tax ID">
          {(id) => (
            <Input
              id={id}
              value={form.taxIdentificationNo ?? ''}
              onChange={(e) => set('taxIdentificationNo', e.target.value)}
            />
          )}
        </Field>
        <Field label="Address line 1">
          {(id) => (
            <Input id={id} value={form.addressLine1 ?? ''} onChange={(e) => set('addressLine1', e.target.value)} />
          )}
        </Field>
        <Field label="Address line 2">
          {(id) => (
            <Input id={id} value={form.addressLine2 ?? ''} onChange={(e) => set('addressLine2', e.target.value)} />
          )}
        </Field>
        <Field label="Address line 3">
          {(id) => (
            <Input id={id} value={form.addressLine3 ?? ''} onChange={(e) => set('addressLine3', e.target.value)} />
          )}
        </Field>
        <Field label="Postcode">
          {(id) => <Input id={id} value={form.postcode ?? ''} onChange={(e) => set('postcode', e.target.value)} />}
        </Field>
        <Field label="Country">
          {(id) => (
            <Input
              id={id}
              value={form.addressCountry ?? ''}
              onChange={(e) => set('addressCountry', e.target.value)}
            />
          )}
        </Field>
      </div>

      <p className="mt-4 text-[12px] text-muted">
        Your registered name and registration number are not here on purpose. They identify
        the company Codemax contracted with, so changing one is a new agreement rather than
        a correction &mdash; speak to your Codemax contact.
      </p>
    </Dialog>
  )
}

function BankDialog({
  open,
  currencyCode,
  onClose,
  onDone,
}: {
  open: boolean
  /** The currency in force, offered as the starting point rather than as the answer. */
  currencyCode: string
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<BankAccountProposal>(() => ({
    currencyCode,
    accountHolderName: '',
    bankName: '',
    branchName: null,
    accountNumber: '',
    swiftBic: null,
  }))

  function set(key: keyof BankAccountProposal, value: string) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  // The same three the API insists on, checked here so the refusal arrives before the
  // round trip rather than after it.
  const ready =
    form.currencyCode.trim().length === 3
    && form.accountHolderName.trim() !== ''
    && form.bankName.trim() !== ''
    && form.accountNumber.trim() !== ''

  async function send() {
    setSaving(true)
    try {
      await api.post('/portal/change-requests', { kind: 'BankAccount', bankAccount: form })
      toast.show('Sent to Codemax', 'success')
      onDone()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not send that request', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      title="Request a change of bank account"
      description="Your current account keeps receiving payouts until this is approved."
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={saving} disabled={!ready} onClick={() => void send()}>
            Send request
          </Button>
        </>
      }
    >
      <div
        role="note"
        className="mb-4 rounded-md border border-accent/20 bg-accent-wash px-4 py-3 text-[13px] text-accent"
      >
        Somebody at Codemax will telephone you on the number already on your file to confirm
        this before anything is paid to the new account. If a call comes from a number you
        do not recognise asking you to confirm a change, it is not us.
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Account holder" required>
          {(id) => (
            <Input
              id={id}
              value={form.accountHolderName}
              onChange={(e) => set('accountHolderName', e.target.value)}
            />
          )}
        </Field>
        <Field label="Currency" required hint="Three letters, e.g. MYR.">
          {(id) => (
            <Input
              id={id}
              value={form.currencyCode}
              maxLength={3}
              onChange={(e) => set('currencyCode', e.target.value.toUpperCase())}
            />
          )}
        </Field>
        <Field label="Bank" required>
          {(id) => <Input id={id} value={form.bankName} onChange={(e) => set('bankName', e.target.value)} />}
        </Field>
        <Field label="Branch">
          {(id) => (
            <Input id={id} value={form.branchName ?? ''} onChange={(e) => set('branchName', e.target.value)} />
          )}
        </Field>
        <Field label="Account number" required>
          {(id) => (
            <Input
              id={id}
              className="tabular"
              // Never autofilled and never remembered. A bank field is the one place a
              // browser's saved value could quietly send money somewhere else.
              autoComplete="off"
              value={form.accountNumber}
              onChange={(e) => set('accountNumber', e.target.value)}
            />
          )}
        </Field>
        <Field label="SWIFT / BIC">
          {(id) => (
            <Input
              id={id}
              className="tabular"
              value={form.swiftBic ?? ''}
              onChange={(e) => set('swiftBic', e.target.value.toUpperCase())}
            />
          )}
        </Field>
      </div>
    </Dialog>
  )
}
